/**
 * Resolves the entity_id of the "To Do" stage in the org's default pipeline for
 * a memo family (`ahoy_note` / `ahoy_task`). Every composer entry point seeds
 * `stageEntityId` from this (useOpenTaskCompose, MemoForm's stage control), so a
 * task created from any surface lands in "To Do" instead of having no stage.
 */

import { useMemo } from 'react';

import { useOrg } from '@/contexts/OrgContext';
import { usePipelines } from '@/lib/crm/hooks/usePipelines';

import type { MemoEntityType } from './notes.utils';

const DEFAULT_STAGE_NAME = 'To Do';

/**
 * Returns the entity_id of the "To Do" stage in the org's default pipeline for
 * `entityType` (the note pipeline on an unmigrated org, the task pipeline for
 * ahoy_task).
 *
 * Three-valued so callers can tell "not yet resolved" from "resolved, no stage":
 * - `undefined` — pipelines are still loading OR the query errored; resolution
 *                 is not yet decided.
 * - `null`      — resolved successfully, but no default stage could be found (a
 *                 genuinely stage-less org — tasks stay creatable, just unstaged).
 * - `string`    — the resolved "To Do" (or first) stage entity_id.
 *
 * Keeping not-yet-resolved distinct from resolved-none lets a task entry point
 * stay pending while stages load, instead of treating the transient null as a
 * stage-less org and opening an unstaged task (see useOpenTaskCompose). A failed
 * query is treated the same as loading — `undefined`, never `null` — so a
 * request error can't silently masquerade as a stage-less org; React Query
 * retries it, and once it succeeds this resolves to a real stage (or `null`).
 */
export function useDefaultMemoStage(
  entityType: MemoEntityType = 'ahoy_note'
): string | null | undefined {
  const { currentOrg } = useOrg();
  const { pipelines, isLoading, error } = usePipelines(
    currentOrg?.id,
    entityType
  );

  return useMemo(() => {
    // Not yet resolved — still loading, the query errored, OR the org itself
    // hasn't loaded yet — undefined, not null, so the caller keeps task
    // creation pending rather than treating an unresolved query as a stage-less
    // org. Without the org guard, `usePipelines(undefined, …)` returns `[]`
    // with `isLoading: false`, so a still-loading org would fall through to the
    // empty-pipelines branch below and resolve to `null` — letting
    // useOpenTaskCompose open an unstaged task before the pipelines load. Only
    // a SUCCESSFUL empty response for a loaded org yields null (a genuinely
    // stage-less org).
    if (isLoading || error || !currentOrg?.id) {
      return undefined;
    }
    if (pipelines.length === 0) {
      return null;
    }
    const defaultPipeline = pipelines.find(p => p.is_default) ?? pipelines[0];
    if (!defaultPipeline) {
      return null;
    }
    // Sort once, then prefer an explicit "To Do" / "to do" match (covers
    // small label edits like trailing whitespace or case changes). If the
    // user renamed the seeded stage entirely, fall back to the first stage
    // by order so new notes still land in a meaningful default state
    // instead of being un-staged.
    const orderedStages = defaultPipeline.stages
      .slice()
      .sort((a, b) => a.properties.order - b.properties.order)
      .filter(s => s.entity_id != null);
    const matchesDefault = (s: (typeof orderedStages)[number]) => {
      const norm = (v: string | undefined) => (v ?? '').trim().toLowerCase();
      const target = DEFAULT_STAGE_NAME.toLowerCase();
      return norm(s.stage_name) === target || norm(s.label) === target;
    };
    const matched = orderedStages.find(matchesDefault);
    return matched?.entity_id ?? orderedStages[0]?.entity_id ?? null;
  }, [isLoading, error, pipelines, currentOrg?.id]);
}
